import { ClientBooking, ClientCampaign, getAllClientBookings, getClientCampaigns } from '@round/api';
import { useCallback, useState } from 'react';

type UseCampaignListDataProps = Parameters<typeof getClientCampaigns>[0];

const PAGE_SIZE = 10;

export function useCampaignListData({ search, status, my_campaigns }: UseCampaignListDataProps) {
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [campaigns, setCampaigns] = useState<ClientCampaign[]>([]);
    const [bookings, setBookings] = useState<ClientBooking[]>([]);

    const [initialized, setInitialized] = useState(false);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [campaignsWithBookingsLoading, setCampaignsWithBookingsLoading] = useState<number[]>([]);
    const [errorFetchingCampaigns, setErrorFetchingCampaigns] = useState(false);

    const fetchBookings = useCallback(async (campaignIds: number[], requestInit?: RequestInit) => {
        if (!campaignIds.length) {
            return;
        }
        try {
            setCampaignsWithBookingsLoading(campaignIds);
            const bookings = await getAllClientBookings({ campaign_id: campaignIds.join(',') }, requestInit);
            setBookings((prev) => prev.concat(bookings));
        } catch {
            //no-op
        } finally {
            setCampaignsWithBookingsLoading([]);
        }
    }, []);

    const fetchCampaignsData = useCallback(
        async (params: Parameters<typeof getClientCampaigns>[0], requestInit?: RequestInit) => {
            try {
                setErrorFetchingCampaigns(false);
                setCampaignsLoading(true);
                const { data: campaignsData } = await getClientCampaigns(
                    { ...params, page_size: PAGE_SIZE },
                    requestInit
                );
                setCampaigns((prev) => prev.concat(campaignsData.results));
                setCampaignsLoading(false);
                setHasNextPage(Boolean(campaignsData.next));

                await fetchBookings(
                    campaignsData.results.map((campaign) => campaign.id),
                    requestInit
                );
            } catch {
                setErrorFetchingCampaigns(true);
            } finally {
                setInitialized(true);
            }
        },
        [fetchBookings]
    );

    const init = useCallback(
        async (requestInit?: RequestInit) => {
            await fetchCampaignsData({ search, status, my_campaigns }, requestInit);
        },
        [fetchCampaignsData, search, status, my_campaigns]
    );

    const reset = useCallback(() => {
        setCampaigns([]);
        setBookings([]);

        setPage(1);
        setInitialized(false);
        setCampaignsLoading(false);
        setHasNextPage(false);
        setErrorFetchingCampaigns(false);
        setCampaignsWithBookingsLoading([]);
    }, []);

    const loadNextPage = useCallback(
        async (requestInit?: RequestInit) => {
            await fetchCampaignsData({ search, status, my_campaigns, page: page + 1 }, requestInit);
            setPage((page) => page + 1);
        },
        [fetchCampaignsData, page, search, my_campaigns, status]
    );

    return {
        campaigns,
        bookings,
        page,
        hasNextPage,
        initialized,
        campaignsLoading,
        campaignsWithBookingsLoading,
        errorFetchingCampaigns,
        init,
        reset,
        loadNextPage,
    };
}
